import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap, Star, Eye, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api, GEN } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const benefits = [
  { Icon: Zap, title: "Early Access", desc: "Read new issues before anyone else — the moment they drop." },
  { Icon: Star, title: "Exclusive Comics", desc: "Members-only stories, variant covers, and full-issue reading." },
  { Icon: Eye, title: "Behind-the-Scenes", desc: "Concept art, creator notes, and the making of the universe." },
];

export const JoinSection = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", { email, name, source: "join" });
      toast.success(data.message);
      setJoined(true);
    } catch {
      toast.error("Could not join. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" className="relative py-24 sm:py-32 overflow-hidden" data-testid="join-section">
      <div className="absolute inset-0">
        <img src={GEN.actionLeap} alt="" className="w-full h-full object-cover object-center opacity-30" loading="lazy" />
        <div className="absolute inset-0 bg-[#050806]/85" />
        <div className="absolute inset-0 halftone opacity-30" />
      </div>

      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="font-accent text-xl tracking-[0.3em] text-emerald-400">JOIN THE MOVEMENT</p>
          <h2 className="comic-title text-5xl sm:text-7xl mt-2">JOIN THE NAIJAMAN UNIVERSE</h2>
          <p className="text-gray-300 mt-4 text-lg">Get free previews and updates. Be first in line for every drop.</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-xl border border-white/10 hover:border-emerald-400/50 bg-black/50 backdrop-blur p-7 transition-colors duration-300"
              data-testid={`benefit-${i}`}
            >
              <div className="w-12 h-12 grid place-items-center rounded-lg bg-emerald-500 text-emerald-950"><b.Icon size={24} /></div>
              <h3 className="font-display text-2xl text-white mt-4">{b.title}</h3>
              <p className="text-gray-400 mt-2">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Signup block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 max-w-2xl mx-auto rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/50 to-black p-8 sm:p-10 text-center"
        >
          {joined ? (
            <div data-testid="join-success">
              <div className="w-14 h-14 mx-auto grid place-items-center rounded-full bg-emerald-500 text-emerald-950"><Check size={28} /></div>
              <p className="font-display text-3xl text-white mt-4">Welcome, hero!</p>
              <p className="text-gray-300 mt-2">You&rsquo;re on the list. Create a free account to unlock members-only content.</p>
              {user === false && (
                <Link to="/register" className="inline-block btn-skew bg-amber-500 hover:bg-amber-400 text-black font-accent text-xl tracking-wide px-8 py-3 mt-6" data-testid="join-create-account-btn">
                  <span>Create Free Account</span>
                </Link>
              )}
            </div>
          ) : (
            <form onSubmit={submit} data-testid="join-form">
              <p className="font-display text-3xl text-white">Get Early Access</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="flex-1 bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                  data-testid="join-name-input"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-white/5 border border-white/15 rounded-md px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400"
                  data-testid="join-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide px-10 py-3 mt-5 glow-green disabled:opacity-60"
                data-testid="join-submit-btn"
              >
                <span className="flex items-center gap-2"><Sparkles size={18} /> {loading ? "Joining..." : "Join Free"}</span>
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
